/**
 * Cable profiles -- group 08.
 *
 * Nirvana Audio, WyWires, Iconoclast, Audio Envy, Cerious Technologies,
 * High Fidelity Cables, Verastarr, Sablon Audio.
 *
 * Iconoclast is the outlier in this group: it is a Belden design programme and
 * it publishes full inductance and capacitance tables, so those entries carry
 * real numbers. Everyone else here publishes construction descriptions and
 * nothing electrical, so the kit derives R, L and C from gauge, material and
 * geometry, and the entry says so.
 *
 * Several brands in this group are sold on claims the engine cannot model --
 * magnetic conduction, graphene compounds, foil geometry. The claims are not
 * repeated here. What is recorded is what the cable is made of and what that
 * construction does to three numbers.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_08: CableProfile[] = [
  // =========================================================================
  // Nirvana Audio -- United States. A small Long Island maker with a narrow
  // range: two tiers of interconnect and speaker cable plus one digital coax.
  // Nothing electrical was ever published, and much of the range now trades
  // secondhand, so prices are approximate and confidence is estimated.
  // =========================================================================
  makeCable({
    mfr: 'Nirvana Audio',
    model: 'S-L Interconnect',
    cableType: 'interconnect',
    price: usd(650, 'APPROXIMATE', 2024, 'Typical asking price for a 1 m pair; mostly a secondary-market item.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#7f6bd8',
    tags: ['entry-line', 'stranded-copper'],
    provenanceNote:
      'Nirvana Audio published no construction sheet and no electrical data. Conductor grade and geometry here are inferred from dealer and review descriptions of a stranded copper twisted pair, and are marked estimated rather than presented as manufacturer specification. All three electrical figures are derived from gauge, material and geometry.',
    notes:
      'Stranded copper in a conventional twisted pair with RCA and XLR terminations. At a derived 70 pF/m a 1 m run presents roughly 70 pF, which even against a 2 kohm tube output stage puts the low-pass corner above 1 MHz. The engine computes the loading and reports what it works out to.',
  }),

  makeCable({
    mfr: 'Nirvana Audio',
    model: 'S-X Ltd Interconnect',
    cableType: 'interconnect',
    price: usd(1500, 'APPROXIMATE', 2024, 'Typical asking price for a 1 m pair; mostly a secondary-market item.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#7f6bd8',
    tags: ['upper-line', 'occ-conductor'],
    provenanceNote:
      'The Ltd version was described as using a higher-purity, larger-section copper than the S-L. That description is the only source for the conductor grade and gauge recorded here, so both are marked estimated. No electrical figures were ever published; all three are derived.',
    notes:
      'The larger-section version of the same twisted pair. The extra copper drops loop resistance by about a fifth relative to the S-L, which for a line-level run feeding a 10 kohm input is far below anything the divider can resolve. The geometry, and therefore the inductance and capacitance, is unchanged.',
  }),

  makeCable({
    mfr: 'Nirvana Audio',
    model: 'Transmission Digital',
    cableType: 'interconnect',
    price: usd(500, 'APPROXIMATE', 2024, 'Typical asking price for a 1 m single coaxial cable.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    characteristicOhms: 75,
    shielding: 'Braided copper shield',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#7f6bd8',
    tags: ['coaxial', 'spdif', 'controlled-impedance'],
    provenanceNote:
      'Sold as a 75 ohm S/PDIF coaxial cable. The characteristic impedance is the design target stated for the product; the per-metre resistance, inductance and capacitance were never published and are derived here from gauge, material and geometry.',
    notes:
      'A single-ended 75 ohm coaxial run for S/PDIF. The number that matters for a coaxial digital link is the impedance match at both ends, since a mismatch produces reflections that eat into the receiver eye pattern. The engine has no S/PDIF transport model, so this entry contributes its construction and its price and no signal processing.',
  }),

  makeCable({
    mfr: 'Nirvana Audio',
    model: 'S-L Speaker Cable',
    cableType: 'speaker',
    price: usd(1200, 'APPROXIMATE', 2024, 'Typical asking price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#7f6bd8',
    tags: ['entry-line', 'braided'],
    provenanceNote:
      'Conductor grade, gauge and braid geometry are inferred from dealer and review descriptions; Nirvana Audio published neither a construction sheet nor electrical figures. All values are derived and the entry is marked estimated throughout.',
    notes:
      'Multiple copper runs woven so that adjacent conductors carry opposing current. That weave is the reason a braided speaker cable lands near 0.4 uH/m instead of the 0.75 uH/m a widely spaced pair would show. Over a 2.5 m run into a 4 ohm minimum the series impedance is still small, and the engine reports the divider it actually forms.',
  }),

  makeCable({
    mfr: 'Nirvana Audio',
    model: 'S-X Ltd Speaker Cable',
    cableType: 'speaker',
    price: usd(2800, 'APPROXIMATE', 2024, 'Typical asking price for a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#7f6bd8',
    tags: ['upper-line', 'braided', 'occ-conductor'],
    provenanceNote:
      'As with the S-L, no construction sheet or electrical data was published. The heavier gauge and higher-purity copper recorded here come from the maker description of the Ltd version and are marked estimated.',
    notes:
      'The same braid in a heavier gauge. Going from 12 to 11 AWG cuts loop resistance by about a quarter, which is the one change here with a computable consequence: on a long run into a low-impedance load, series resistance is what flattens the amplifier damping factor seen at the driver.',
  }),

  // =========================================================================
  // WyWires -- United States, Los Angeles. Litz-configured conductors across
  // four tiers. The maker describes geometry and dielectric in detail but
  // publishes no R, L or C.
  // =========================================================================
  makeCable({
    mfr: 'WyWires',
    model: 'Blue Series Speaker Cable',
    cableType: 'speaker',
    price: usd(749, 'APPROXIMATE', 2024, 'Roughly a 2.4 m (8 ft) terminated pair; price scales with length and termination.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 14,
    dielectric: 'Low-density polymer with air voids',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['entry-line', 'litz'],
    provenanceNote:
      'WyWires describes the litz configuration and the dielectric but publishes no resistance, inductance or capacitance. Conductor material and gauge here are inferred from the series description and are marked estimated; all electrical values are derived.',
    notes:
      'Individually insulated strands in a litz configuration, which keeps every strand a separate conducting path rather than letting the bundle behave as one solid conductor at high frequency. At 14 AWG a 2.4 m run adds about 0.02 ohm of loop resistance, which against an 8 ohm nominal load is a divider loss of roughly 0.02 dB.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Silver Series Speaker Cable',
    cableType: 'speaker',
    price: usd(1899, 'APPROXIMATE', 2024, 'Roughly a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'litz',
    gauge: 13,
    dielectric: 'Low-density polymer with air voids',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['mid-line', 'litz', 'silver-plated'],
    provenanceNote:
      'Series tier and construction are as described by the maker; the conductor material recorded here is inferred from the series naming and description rather than from a published specification, and is marked estimated. No electrical figures are published, so all three are derived.',
    notes:
      'Silver-plated copper litz. The plating is a skin layer, so the DC path is still the copper core and the loop resistance the kit derives uses copper resistivity accordingly. The step up from the Blue is one gauge of extra section, worth about 20 percent less loop resistance.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Platinum Series Speaker Cable',
    cableType: 'speaker',
    price: usd(4500, 'APPROXIMATE', 2024, 'Roughly a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'litz',
    gauge: 12,
    dielectric: 'Low-density polymer with air voids',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['upper-line', 'litz', 'hybrid-conductor'],
    provenanceNote:
      'Conductor material is inferred from the maker description of copper and silver conductors used together in the upper series, not from a published specification, and is marked estimated. All electrical figures are derived.',
    notes:
      'A mixed copper and silver litz bundle at 12 AWG. Mixing the two materials changes bulk resistivity by a few percent at most, so the meaningful difference against the lower tiers is the extra conductor section, not the metal. The litz geometry puts inductance near 0.35 uH/m.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Diamond Series Speaker Cable',
    cableType: 'speaker',
    price: usd(8500, 'APPROXIMATE', 2024, 'Roughly a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'litz',
    gauge: 11,
    dielectric: 'Low-density polymer with air voids',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['flagship', 'litz', 'hybrid-conductor'],
    provenanceNote:
      'Flagship series. Conductor material and gauge are inferred from the maker description and marked estimated; no electrical data is published, so all values are derived.',
    notes:
      'The heaviest litz bundle in the range at an 11 AWG equivalent, giving roughly 0.008 ohm/m loop resistance. That is the one figure in this cable that a measurement would separate from the tier below it; the geometry, and therefore the inductance and capacitance, is common to the whole series.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Blue Series Interconnect',
    cableType: 'interconnect',
    price: usd(399, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 22,
    dielectric: 'Low-density polymer with air voids',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['entry-line', 'litz'],
    provenanceNote:
      'Construction described by the maker; conductor material and gauge inferred and marked estimated. No published electrical figures, so all three are derived.',
    notes:
      'Litz interconnect offered single-ended and balanced. A derived 120 pF/m means a 1 m run loads the source with roughly 120 pF; against a 100 ohm solid-state output that is a corner above 13 MHz, and against a 2 kohm tube output still well above 600 kHz. The engine reports the in-band result, which is close to nothing.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Silver Series Interconnect',
    cableType: 'interconnect',
    price: usd(999, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'litz',
    gauge: 22,
    dielectric: 'Low-density polymer with air voids',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['mid-line', 'litz', 'silver-plated'],
    provenanceNote:
      'Conductor material inferred from the series description rather than a published specification and marked estimated. Electrical figures derived from gauge, material and geometry.',
    notes:
      'Silver-plated copper litz in the same geometry as the Blue interconnect. Identical gauge and identical geometry mean the derived inductance and capacitance are identical too; the plating changes the surface metal, not the loop the engine solves.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Platinum Series Interconnect',
    cableType: 'interconnect',
    price: usd(2400, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'litz',
    gauge: 21,
    dielectric: 'Low-density polymer with air voids',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['upper-line', 'litz', 'hybrid-conductor'],
    provenanceNote:
      'Conductor material inferred from the maker description of copper and silver used together in the upper series; marked estimated. No published electrical data.',
    notes:
      'A heavier litz bundle than the lower tiers, at a 21 AWG equivalent. Series resistance in a line-level interconnect feeding a 10 kohm input is four orders of magnitude below the load, so the extra section has no computable signal consequence; it is a build choice, not an electrical one.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Diamond Series Interconnect',
    cableType: 'interconnect',
    price: usd(4900, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'litz',
    gauge: 21,
    dielectric: 'Low-density polymer with air voids',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['flagship', 'litz', 'hybrid-conductor'],
    provenanceNote:
      'Flagship interconnect. Conductor material and gauge inferred from maker descriptions and marked estimated; all electrical values derived.',
    notes:
      'The top litz interconnect in the range, with the same conductor geometry as the Platinum and heavier terminations. Because the geometry is shared, the capacitance the engine loads the source with is the same as the tier below.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Juice II Power Cord',
    cableType: 'power',
    price: usd(599, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['high-current', 'litz'],
    provenanceNote:
      'Gauge and current rating are inferred from the maker description of a heavy litz mains cord; neither appears on a published specification sheet, so the entry is marked estimated.',
    notes:
      'A 12 AWG litz mains cord rated for a 15 A circuit, with moulded IEC and wall ends. The engine applies no signal processing to power cords, so this entry contributes its construction, its rating and its price and leaves the audio bit-identical.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Juice III Power Cord',
    cableType: 'power',
    price: usd(1299, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 11,
    currentRatingA: 20,
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['high-current', 'litz', 'c19-option'],
    provenanceNote:
      'Gauge, current rating and connector options are inferred from the maker description rather than a published sheet, and are marked estimated.',
    notes:
      'Heavier gauge than the Juice II with a C19 option for equipment that draws enough to need it. An 11 AWG cord has roughly 20 percent less voltage drop under a large current draw than a 12 AWG one, which matters at the inlet of a big power amplifier. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'WyWires',
    model: 'Platinum Series USB',
    cableType: 'usb',
    price: usd(699, 'APPROXIMATE', 2024, 'Roughly a 1 m assembly.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#3a6ea5',
    tags: ['usb-2.0', 'shielded'],
    provenanceNote:
      'Conductor material, gauge and shield construction are inferred from the maker description; no compliance report or electrical sheet is published, so the entry is marked estimated.',
    notes:
      'A shielded 90 ohm USB 2.0 assembly with a 24 AWG data pair and separate power conductors. The engine applies no signal processing to USB. USB audio is packetised and error-checked, and an asynchronous DAC generates its own clock, so a compliant cable either carries the data or fails to.',
  }),

  // =========================================================================
  // Iconoclast -- United States. A Belden design programme sold through Blue
  // Jeans Cable. Unusually for a boutique-priced cable line, full inductance
  // and capacitance tables are published, so the entries below carry real
  // numbers rather than derived ones wherever the published figure is known.
  // =========================================================================
  makeCable({
    mfr: 'Iconoclast',
    model: 'Series 1 TPC Speaker Cable',
    cableType: 'speaker',
    price: usd(1250, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair; price scales with length.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 12,
    inductanceUhPerM: 0.26,
    capacitancePfPerM: 148,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource:
      'Iconoclast published design data for the 1x4 speaker cable (0.08 uH/ft, 45 pF/ft). Loop resistance derived from the 12 AWG equivalent copper section.',
    dielectric: 'Foamed polyethylene over individually insulated conductors',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-design', 'entry-line'],
    notes:
      'A 1x4 array of individually insulated small-gauge tough-pitch copper conductors, arranged so that current is shared across many parallel paths rather than one heavy one. The published 0.08 uH/ft works out to 0.26 uH/m, roughly a third of what a widely spaced pair would give, and the 45 pF/ft that comes with it is the price of that spacing. This is one of the few cables at this price with a public electrical table.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'Series 1 SPTPC Speaker Cable',
    cableType: 'speaker',
    price: usd(1650, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'litz',
    gauge: 12,
    inductanceUhPerM: 0.26,
    capacitancePfPerM: 148,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource:
      'Iconoclast published design data for the 1x4 speaker cable (0.08 uH/ft, 45 pF/ft). Loop resistance derived from the 12 AWG equivalent copper section.',
    dielectric: 'Foamed polyethylene over individually insulated conductors',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-design', 'silver-plated'],
    notes:
      'The same 1x4 geometry as the TPC version with silver-plated conductors. Because the geometry is identical the published inductance and capacitance are identical; the plating is a skin layer and the DC loop path remains the copper core. Anyone expecting a measurable electrical difference between these two should note that the maker publishes the same numbers for both.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'Series 2 TPC Speaker Cable',
    cableType: 'speaker',
    price: usd(2200, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'litz',
    gauge: 9,
    inductanceUhPerM: 0.13,
    capacitancePfPerM: 296,
    electricalConfidence: 'DERIVED',
    electricalSource:
      'Two Series 1 arrays paralleled per polarity: loop resistance halves, inductance halves and capacitance doubles relative to the published 1x4 figures. Derived by that rule, not separately published.',
    dielectric: 'Foamed polyethylene over individually insulated conductors',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-design', 'double-array'],
    notes:
      'The 2x4 build: twice the conductor count of the Series 1 in the same arrangement. Paralleling halves both resistance and inductance and doubles capacitance, which is the standard result and the reason the figures here are marked derived rather than published. A 2.5 m run lands near 0.005 ohm of loop resistance.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'Series 2 SPTPC Speaker Cable',
    cableType: 'speaker',
    price: usd(2900, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'litz',
    gauge: 9,
    inductanceUhPerM: 0.13,
    capacitancePfPerM: 296,
    electricalConfidence: 'DERIVED',
    electricalSource:
      'Two Series 1 arrays paralleled per polarity, applied to the published 1x4 figures. Derived by that rule, not separately published.',
    dielectric: 'Foamed polyethylene over individually insulated conductors',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-design', 'double-array', 'silver-plated'],
    notes:
      'Top of the Iconoclast speaker range: the doubled array in silver-plated conductors. Of the three numbers the engine reads, only loop resistance separates this from the Series 1, and it does so by a factor of two. That is a real difference on a long run into a 3 ohm minimum and a negligible one on a short run into 8 ohms.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'BAV Speaker Cable',
    cableType: 'speaker',
    price: usd(180, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair built from bulk Belden cable.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#d1603d',
    tags: ['belden-based', 'value-line'],
    provenanceNote:
      'The BAV line is assembled from standard Belden bulk cable, and Belden publishes full data for each of its part numbers. The specific part number behind this assembly is not confirmed here, so no published figures are transcribed and the electrical values are derived from a 12 AWG twisted pair. Marked estimated for that reason.',
    notes:
      'The value end of the range: ordinary heavy-gauge Belden bulk cable terminated properly. At 12 AWG the loop resistance over 2.5 m is about 0.026 ohm, which is what actually sets the damping factor the driver sees, and it is within a hair of what the four-figure cables above deliver.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'Generation 1 TPC Interconnect',
    cableType: 'interconnect',
    price: usd(750, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 22,
    inductanceUhPerM: 0.36,
    capacitancePfPerM: 41,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource:
      'Iconoclast published interconnect design data (about 12.5 pF/ft and 0.11 uH/ft). Loop resistance derived from the 22 AWG equivalent copper section.',
    dielectric: 'Air-spaced polymer over individually insulated conductors',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-design', 'low-capacitance'],
    notes:
      'Many individually insulated small conductors in a helical array rather than a single centre conductor. The published 12.5 pF/ft is about 41 pF/m, which is low for a shielded interconnect and roughly a third of what a typical litz or braided design shows. Against a 2 kohm tube output a 1 m run corners near 1.9 MHz.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'Generation 2 SPTPC Interconnect',
    cableType: 'interconnect',
    price: usd(1050, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 22,
    inductanceUhPerM: 0.36,
    capacitancePfPerM: 41,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource:
      'Iconoclast published interconnect design data (about 12.5 pF/ft and 0.11 uH/ft). Loop resistance derived from the 22 AWG equivalent copper section.',
    dielectric: 'Air-spaced polymer over individually insulated conductors',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-design', 'silver-plated', 'low-capacitance'],
    notes:
      'The revised array in silver-plated tough-pitch copper. The published capacitance and inductance are unchanged from the first generation because the conductor arrangement that sets them is unchanged. What changed is the plating and the assembly, neither of which appears in the three numbers the engine reads.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'UP-OCC Copper Interconnect',
    cableType: 'interconnect',
    price: usd(1500, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 22,
    inductanceUhPerM: 0.36,
    capacitancePfPerM: 41,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource:
      'Iconoclast published interconnect design data (about 12.5 pF/ft and 0.11 uH/ft) for the shared conductor array. Loop resistance derived from the 22 AWG equivalent section using OCC copper resistivity.',
    dielectric: 'Air-spaced polymer over individually insulated conductors',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-design', 'occ-conductor', 'low-capacitance'],
    notes:
      'The same published array drawn in continuous-cast copper. OCC copper is about one percent lower in bulk resistivity than standard electrolytic copper, so on a line-level run the difference is far below anything the divider can express. The geometry, which is what sets the 41 pF/m, is identical.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'UP-OCC Silver Interconnect',
    cableType: 'interconnect',
    price: usd(4200, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'braided',
    gauge: 22,
    inductanceUhPerM: 0.36,
    capacitancePfPerM: 41,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource:
      'Iconoclast published interconnect design data (about 12.5 pF/ft and 0.11 uH/ft) for the shared conductor array. Loop resistance derived from the 22 AWG equivalent section using silver resistivity.',
    dielectric: 'Air-spaced polymer over individually insulated conductors',
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-design', 'solid-silver', 'flagship'],
    notes:
      'The flagship interconnect: the published array drawn in solid silver. Silver is about eight percent lower in resistivity than copper, which is the entire electrical consequence of the material change and amounts to a few milliohms over a metre. The cost is dominated by the metal, not by anything the engine computes.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'BAV RCA Interconnect',
    cableType: 'interconnect',
    price: usd(75, 'CURRENT_RETAIL', 2024, 'Price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 18,
    characteristicOhms: 75,
    capacitancePfPerM: 53,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Belden 1694A datasheet (16.2 pF/ft), the bulk cable the BAV RCA assembly is built from.',
    dielectric: 'Gas-injected foam polyethylene',
    shielding: 'Foil plus tinned copper braid',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-based', 'coaxial', 'value-line'],
    notes:
      'Belden 1694A precision video coax terminated as an analog interconnect. Its 16.2 pF/ft is 53 pF/m, so a 1 m run loads the source with about 53 pF -- lower than most of the four-figure cables in this file, because a foam-dielectric coax is simply a good low-capacitance structure. The 18 AWG solid centre conductor is heavier than most interconnects use.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'BAV XLR Interconnect',
    cableType: 'interconnect',
    price: usd(70, 'CURRENT_RETAIL', 2024, 'Price for a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    capacitancePfPerM: 39,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Belden 1800F datasheet (about 12 pF/ft conductor to conductor), the bulk cable the BAV XLR assembly is built from.',
    dielectric: 'Foam polyethylene',
    shielding: 'Foil with drain wire, 100 percent coverage',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-based', 'balanced', 'value-line'],
    notes:
      'Belden 1800F, a low-capacitance 24 AWG balanced pair designed for AES/EBU and used here for analog balanced runs. At 39 pF/m it is among the lowest-capacitance shielded pairs available, and the foil-and-drain shield gives full coverage. Balanced operation also means common-mode noise on the pair cancels at the receiver.',
  }),

  makeCable({
    mfr: 'Iconoclast',
    model: 'BAV Digital Coax',
    cableType: 'interconnect',
    price: usd(65, 'CURRENT_RETAIL', 2024, 'Price for a 1 m single cable.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 18,
    characteristicOhms: 75,
    capacitancePfPerM: 53,
    electricalConfidence: 'MANUFACTURER_SPEC',
    electricalSource: 'Belden 1694A datasheet (16.2 pF/ft, 75 ohm nominal), the bulk cable the BAV digital assembly is built from.',
    dielectric: 'Gas-injected foam polyethylene',
    shielding: 'Foil plus tinned copper braid',
    termination: ['RCA', 'BNC'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d1603d',
    tags: ['published-specs', 'belden-based', 'coaxial', 'spdif'],
    notes:
      'The same Belden 1694A cut and terminated for S/PDIF, with a BNC option that actually holds 75 ohms through the connector where an RCA does not. Impedance accuracy is the property that matters on a digital coaxial link, since a mismatch reflects energy back and closes the receiver eye. The engine has no S/PDIF transport model and applies no signal processing here.',
  }),

  // =========================================================================
  // Audio Envy -- United States, Colorado. Solid-core copper, many conductors
  // in parallel, sold direct at prices well under the boutique norm. No
  // electrical data is published.
  // =========================================================================
  makeCable({
    mfr: 'Audio Envy',
    model: 'SP AU8',
    cableType: 'speaker',
    price: usd(299, 'CURRENT_RETAIL', 2024, 'Price for a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 14,
    dielectric: 'Polyethylene over each solid conductor',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#4caf7d',
    tags: ['entry-line', 'solid-conductor', 'direct-sale'],
    provenanceNote:
      'Audio Envy states conductor count, solid-core construction and country of manufacture but publishes no resistance, inductance or capacitance and no gauge table. Gauge here is inferred from the conductor count and is marked estimated; all electrical figures are derived.',
    notes:
      'Eight individually insulated solid copper conductors, split four per polarity and interleaved so that opposing runs sit adjacent. Interleaving is what keeps the derived inductance near 0.4 uH/m instead of the 0.75 uH/m a simple separated pair gives, and the cost is capacitance a little over twice as high.',
  }),

  makeCable({
    mfr: 'Audio Envy',
    model: 'SP AU12',
    cableType: 'speaker',
    price: usd(499, 'CURRENT_RETAIL', 2024, 'Price for a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'Polyethylene over each solid conductor',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#4caf7d',
    tags: ['mid-line', 'solid-conductor', 'direct-sale'],
    provenanceNote:
      'Conductor count and solid-core construction are stated by the maker; the equivalent gauge recorded here is inferred from that count and marked estimated. No electrical figures are published.',
    notes:
      'Twelve solid conductors in the same interleaved arrangement, giving roughly a 12 AWG equivalent section. Over a 2.4 m run that is about 0.025 ohm of loop resistance, which against a load dipping to 3 ohms is a divider loss of a little under 0.1 dB at the dip.',
  }),

  makeCable({
    mfr: 'Audio Envy',
    model: 'SP AU24',
    cableType: 'speaker',
    price: usd(899, 'CURRENT_RETAIL', 2024, 'Price for a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 10,
    dielectric: 'Polyethylene over each solid conductor',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#4caf7d',
    tags: ['upper-line', 'solid-conductor', 'direct-sale'],
    provenanceNote:
      'Equivalent gauge inferred from the stated conductor count; no published electrical data. Marked estimated throughout.',
    notes:
      'The heaviest cable in the range, with twenty-four solid conductors making roughly a 10 AWG equivalent. Loop resistance falls to about 0.0066 ohm/m, which is the whole point of the extra copper: on long runs into low-impedance loads, series resistance is the term that degrades the effective damping factor.',
  }),

  makeCable({
    mfr: 'Audio Envy',
    model: 'IC AU2',
    cableType: 'interconnect',
    price: usd(149, 'CURRENT_RETAIL', 2024, 'Price for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'Polyethylene over each solid conductor',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#4caf7d',
    tags: ['entry-line', 'solid-conductor', 'direct-sale'],
    provenanceNote:
      'Construction described by the maker as solid-core copper in a twisted configuration; gauge is inferred and marked estimated. No electrical figures published, so all three are derived.',
    notes:
      'A solid-core twisted pair terminated single-ended. A derived 70 pF/m means a 1 m run loads the source with roughly 70 pF; against a 100 ohm output stage the low-pass corner is above 20 MHz. The engine computes it and reports what it comes to.',
  }),

  makeCable({
    mfr: 'Audio Envy',
    model: 'IC AU4',
    cableType: 'interconnect',
    price: usd(249, 'CURRENT_RETAIL', 2024, 'Price for a 1 m XLR pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 22,
    dielectric: 'Polyethylene over each solid conductor',
    shielding: 'Braided copper shield',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#4caf7d',
    tags: ['mid-line', 'solid-conductor', 'balanced'],
    provenanceNote:
      'Star-quad arrangement and solid-core conductors are stated by the maker; gauge is inferred and marked estimated. No published electrical data.',
    notes:
      'Four solid conductors paralleled in opposing pairs, which is the standard star-quad trick: the two legs of each polarity sit on opposite corners so that an external magnetic field induces equal and opposite voltages that cancel. It also lowers inductance to about 0.45 uH/m relative to a plain twisted pair.',
  }),

  makeCable({
    mfr: 'Audio Envy',
    model: 'IC AU6',
    cableType: 'interconnect',
    price: usd(399, 'CURRENT_RETAIL', 2024, 'Price for a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 21,
    dielectric: 'Polyethylene over each solid conductor',
    shielding: 'Braided copper shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#4caf7d',
    tags: ['upper-line', 'solid-conductor', 'braided'],
    provenanceNote:
      'Conductor count is stated by the maker; equivalent gauge and the braid classification are inferred and marked estimated. No published electrical data.',
    notes:
      'Six solid conductors woven rather than simply twisted, in both single-ended and balanced terminations. The weave puts inductance near 0.4 uH/m and capacitance near 105 pF/m -- the same trade every geometry in this file makes, just at a different point on the curve.',
  }),

  makeCable({
    mfr: 'Audio Envy',
    model: 'P6',
    cableType: 'power',
    price: usd(199, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#4caf7d',
    tags: ['entry-line', 'solid-conductor'],
    provenanceNote:
      'Conductor count and solid-core construction are stated by the maker; equivalent gauge and current rating are inferred from that description and marked estimated.',
    notes:
      'A six-conductor mains cord with moulded C13 and 5-15 ends, at roughly a 14 AWG equivalent per leg and suitable for a 15 A circuit. The engine applies no signal processing to power cords, so this entry changes the chain description and the price and nothing in the audio.',
  }),

  makeCable({
    mfr: 'Audio Envy',
    model: 'P8',
    cableType: 'power',
    price: usd(299, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#4caf7d',
    tags: ['mid-line', 'solid-conductor', 'high-current'],
    provenanceNote:
      'Equivalent gauge and current rating are inferred from the stated conductor count and marked estimated; no published sheet.',
    notes:
      'Eight conductors giving roughly a 12 AWG equivalent per leg, rated for a 15 A circuit. Heavier legs mean less voltage drop under the peak currents an amplifier draws on transients. No signal processing is applied to power cords by the engine.',
  }),

  makeCable({
    mfr: 'Audio Envy',
    model: 'P12',
    cableType: 'power',
    price: usd(449, 'CURRENT_RETAIL', 2024, 'Price for a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#4caf7d',
    tags: ['upper-line', 'solid-conductor', 'high-current', 'c19-option'],
    provenanceNote:
      'Equivalent gauge, current rating and connector options are inferred from the maker description and marked estimated.',
    notes:
      'The heaviest cord in the range at roughly a 10 AWG equivalent, with a C19 option for equipment with a 20 A inlet. That gauge is what a large monoblock inlet actually wants. The engine applies no signal processing to power cords and this entry is here for the build and the price.',
  }),

  // =========================================================================
  // Cerious Technologies -- United States, Texas. The distinguishing feature is
  // the packed compound surrounding the conductors, not the conductors
  // themselves. No gauge, no conductor spec and no electrical data are
  // published, so every entry here is estimated and every figure derived.
  // =========================================================================
  makeCable({
    mfr: 'Cerious Technologies',
    model: 'Nano Signature Interconnect',
    cableType: 'interconnect',
    price: usd(600, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; sold direct with prices that vary by run.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'Packed ceramic-loaded compound surrounding the conductor',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a6d3b',
    tags: ['entry-line', 'ceramic-dielectric'],
    provenanceNote:
      'Cerious publishes no conductor gauge, no conductor specification and no electrical figures. The copper conductor, the 22 AWG gauge and the paired geometry recorded here are inferred from the product description of a flexible tube-filled cable; all are marked estimated and all electrical values are derived.',
    notes:
      'The earlier line, built as a flexible tube with a ceramic-loaded compound packed around the conductor in place of a conventional extruded jacket. The compound is the dielectric, and dielectric constant is one of the two terms that set capacitance -- the other being spacing. Without a published figure the kit derives capacitance from geometry alone.',
  }),

  makeCable({
    mfr: 'Cerious Technologies',
    model: 'Nano Signature Speaker Cable',
    cableType: 'speaker',
    price: usd(1200, 'APPROXIMATE', 2024, 'Roughly a 2.5 m pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    dielectric: 'Packed ceramic-loaded compound surrounding the conductor',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a6d3b',
    tags: ['entry-line', 'ceramic-dielectric'],
    provenanceNote:
      'No gauge and no electrical figures are published. Gauge is inferred from the physical size of the assembly and is marked estimated; resistance, inductance and capacitance are all derived.',
    notes:
      'Separate positive and negative runs, each a tube of ceramic-loaded compound around the conductor, loosely paired along their length. Because the two runs are not held tightly together, the derived inductance sits at the higher end for a speaker cable at about 0.6 uH/m; over 2.5 m that is 1.5 uH, or about 0.13 ohm of reactance at 20 kHz.',
  }),

  makeCable({
    mfr: 'Cerious Technologies',
    model: 'Nano Signature Power Cable',
    cableType: 'power',
    price: usd(600, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    dielectric: 'Packed ceramic-loaded compound surrounding the conductor',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#8a6d3b',
    tags: ['entry-line', 'ceramic-dielectric'],
    provenanceNote:
      'Gauge and current rating are inferred from the physical build; the maker publishes neither. Marked estimated.',
    notes:
      'A mains cord built the same way as the rest of the line, with the compound packed around conductors of roughly 12 AWG equivalent and standard IEC and wall terminations. The engine applies no signal processing to power cords, so nothing in this description is offered as an audible claim.',
  }),

  makeCable({
    mfr: 'Cerious Technologies',
    model: 'Graphene Extreme Interconnect',
    cableType: 'interconnect',
    price: usd(1000, 'APPROXIMATE', 2024, 'Roughly a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    dielectric: 'Packed graphene-loaded compound surrounding the conductor',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a6d3b',
    tags: ['mid-line', 'graphene-dielectric'],
    provenanceNote:
      'The graphene compound is described by the maker as the material packed around the conductor. Conductor metal, gauge and geometry are inferred and marked estimated, and no electrical figures are published.',
    notes:
      'The compound around the conductor changes from ceramic to a graphene-loaded mix. The engine reads resistance, inductance and capacitance and nothing else, and none of those three can be computed from a filler description, so they are derived from the same gauge and geometry as the line below. That is the honest position rather than a flattering one.',
  }),

  makeCable({
    mfr: 'Cerious Technologies',
    model: 'Graphene Extreme Speaker Cable',
    cableType: 'speaker',
    price: usd(2000, 'APPROXIMATE', 2024, 'Roughly a 2.5 m pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    dielectric: 'Packed graphene-loaded compound surrounding the conductor',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a6d3b',
    tags: ['mid-line', 'graphene-dielectric'],
    provenanceNote:
      'Gauge inferred from the physical size of the assembly; no published electrical data. Marked estimated.',
    notes:
      'A heavier conductor than the Nano Signature in the same tube-and-compound build. The gauge step to roughly 11 AWG is the change with a computable consequence: loop resistance drops to about 0.008 ohm/m, which is what sets the divider against a loudspeaker impedance minimum.',
  }),

  makeCable({
    mfr: 'Cerious Technologies',
    model: 'Graphene Extreme Power Cable',
    cableType: 'power',
    price: usd(1200, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    dielectric: 'Packed graphene-loaded compound surrounding the conductor',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#8a6d3b',
    tags: ['mid-line', 'graphene-dielectric'],
    provenanceNote:
      'Gauge and current rating inferred from the physical build; neither is published. Marked estimated.',
    notes:
      'A stiff, heavy mains cord at roughly an 11 AWG equivalent with the graphene compound packed around the conductors. Worth knowing before ordering: cables built this way are much less flexible than an extruded cord and need clearance behind the rack. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Cerious Technologies',
    model: 'Graphene Matrix Interconnect',
    cableType: 'interconnect',
    price: usd(1800, 'APPROXIMATE', 2024, 'Roughly a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    dielectric: 'Packed graphene-loaded compound in the revised matrix arrangement',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8a6d3b',
    tags: ['upper-line', 'graphene-dielectric'],
    provenanceNote:
      'The Matrix line is the maker revised arrangement of the same compound and conductor scheme. Conductor metal, gauge and geometry are inferred and marked estimated; nothing electrical is published.',
    notes:
      'The upper line, using a revised internal arrangement of the same materials. Nothing in the published description pins down conductor spacing, which is the term that would move inductance and capacitance, so those two are derived from the geometry class rather than claimed.',
  }),

  makeCable({
    mfr: 'Cerious Technologies',
    model: 'Graphene Matrix Speaker Cable',
    cableType: 'speaker',
    price: usd(3600, 'APPROXIMATE', 2024, 'Roughly a 2.5 m pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    dielectric: 'Packed graphene-loaded compound in the revised matrix arrangement',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8a6d3b',
    tags: ['upper-line', 'graphene-dielectric'],
    provenanceNote:
      'Gauge inferred from the physical size of the assembly; no published electrical data. Marked estimated.',
    notes:
      'The largest speaker cable in the range at roughly a 10 AWG equivalent, giving about 0.0066 ohm/m of loop resistance. Over a 2.5 m run that is 0.017 ohm in series with the driver, which against a 4 ohm load costs about 0.04 dB and raises the amplifier source impedance seen at the terminals by the same amount.',
  }),

  makeCable({
    mfr: 'Cerious Technologies',
    model: 'Graphene Matrix Power Cable',
    cableType: 'power',
    price: usd(2200, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    dielectric: 'Packed graphene-loaded compound in the revised matrix arrangement',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#8a6d3b',
    tags: ['upper-line', 'graphene-dielectric', 'high-current', 'c19-option'],
    provenanceNote:
      'Gauge, current rating and connector options are inferred from the build description and marked estimated.',
    notes:
      'The heaviest mains cord in the range, with a C19 option for 20 A inlets. It is a genuinely substantial piece of construction. The engine applies exactly zero signal processing to power cords, and that is not a criticism of the cable -- it is what the physics supports.',
  }),

  // =========================================================================
  // High Fidelity Cables -- United States, Texas. Every cable in the range is
  // built around magnet assemblies at the terminations. What the engine reads
  // is the conductor: its resistance, its inductance and its capacitance.
  // =========================================================================
  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'Reveal Interconnect',
    cableType: 'interconnect',
    price: usd(599, 'APPROXIMATE', 2024, 'Roughly a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['entry-line', 'magnet-terminations'],
    provenanceNote:
      'High Fidelity Cables publishes magnet configuration and series tier but no conductor gauge, material or electrical data. Conductor material, gauge and the coaxial classification are inferred from the physical build and marked estimated; all electrical figures are derived.',
    notes:
      'The entry series: a single centre conductor inside a shield, with permanent magnet assemblies built into the terminations. The magnets sit around the conductor at each end and add considerable mass at the plug. A static magnetic field does not alter the resistance, inductance or capacitance of a copper conductor, and those three are what the engine solves for.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'Reveal Speaker Cable',
    cableType: 'speaker',
    price: usd(999, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['entry-line', 'magnet-terminations'],
    provenanceNote:
      'Conductor material and gauge are inferred from the physical build; the maker publishes neither, nor any electrical figures. Marked estimated.',
    notes:
      'A twisted copper pair with magnet assemblies at each termination. At roughly 13 AWG a 2.5 m run adds about 0.033 ohm of loop resistance, which is the figure that matters into a 4 ohm load. The heavy magnet housings also mean the terminations want supporting rather than hanging off a binding post.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'Reveal Power Cable',
    cableType: 'power',
    price: usd(699, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['entry-line', 'magnet-terminations'],
    provenanceNote:
      'Gauge and current rating are inferred from the physical build and marked estimated; the maker publishes neither.',
    notes:
      'A mains cord in the entry series with magnet assemblies at the IEC end. Roughly 13 AWG conductors and standard 15 A terminations. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'CT-1 Interconnect',
    cableType: 'interconnect',
    price: usd(1500, 'APPROXIMATE', 2024, 'Roughly a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['mid-line', 'magnet-terminations'],
    provenanceNote:
      'Conductor material, gauge and geometry are inferred from the physical build; the maker publishes no conductor or electrical specification. Marked estimated.',
    notes:
      'The long-running mid series, with larger magnet assemblies than the Reveal. Construction of the conductor itself is unchanged in any way the maker states, so the derived resistance, inductance and capacitance are the same as the entry series. That is what the available information supports.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'CT-1 Ultimate Interconnect',
    cableType: 'interconnect',
    price: usd(3000, 'APPROXIMATE', 2024, 'Roughly a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['mid-line', 'magnet-terminations', 'occ-conductor'],
    provenanceNote:
      'The Ultimate designation refers to a larger magnet assembly and an upgraded conductor. The conductor grade recorded here is inferred from that description and marked estimated; no gauge or electrical figure is published.',
    notes:
      'The CT-1 with the larger magnet housing and an upgraded conductor. Continuous-cast copper is about one percent lower in resistivity than standard electrolytic copper, so over a 1 m line-level run the difference in series resistance is a fraction of a milliohm against a 10 kohm input.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'CT-1 Speaker Cable',
    cableType: 'speaker',
    price: usd(3200, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['mid-line', 'magnet-terminations'],
    provenanceNote:
      'Conductor material and gauge inferred from the physical build and marked estimated; no published electrical data.',
    notes:
      'The mid-series speaker cable, at roughly 12 AWG with magnet assemblies at each end. A plain twisted pair sits near 0.6 uH/m, so over 2.5 m the series inductance is about 1.5 uH -- around 0.19 ohm of reactance at 20 kHz, which against a 4 ohm load is a computable fraction of a decibel at the top of the band.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'CT-2 Interconnect',
    cableType: 'interconnect',
    price: usd(5000, 'APPROXIMATE', 2024, 'Roughly a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 21,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['upper-line', 'magnet-terminations', 'occ-conductor'],
    provenanceNote:
      'Conductor grade and gauge are inferred from the maker description of a larger conductor and magnet array in the CT-2; both are marked estimated. No electrical figures published.',
    notes:
      'A larger magnet array than the CT-1 and a heavier conductor. The plugs are large and heavy enough that back-panel clearance is a real consideration on closely spaced RCA jacks. Series resistance in a line-level interconnect is negligible against any normal input impedance, so the gauge step has no computable signal consequence.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'CT-2 Speaker Cable',
    cableType: 'speaker',
    price: usd(9000, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['upper-line', 'magnet-terminations', 'occ-conductor'],
    provenanceNote:
      'Conductor grade and gauge inferred from the maker description of the CT-2 build and marked estimated; nothing electrical is published.',
    notes:
      'The upper-series speaker cable, heavier at roughly 11 AWG, which brings derived loop resistance down to about 0.008 ohm/m. The magnet housings at each end are substantial and the cable is correspondingly stiff near the terminations.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'Ultimate Reference Interconnect',
    cableType: 'interconnect',
    price: usd(14000, 'APPROXIMATE', 2024, 'Roughly a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'coaxial',
    gauge: 20,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['reference-line', 'magnet-terminations', 'hybrid-conductor'],
    provenanceNote:
      'Conductor material and gauge are inferred from the maker description of the reference series and marked estimated. No gauge table and no electrical data are published.',
    notes:
      'The reference series, with the largest magnet arrays in the interconnect range and a mixed copper and silver conductor. Mixing the two metals moves bulk resistivity by a few percent; against a 10 kohm input the resulting change in the divider is below any threshold the engine can resolve, and it reports that rather than dressing it up.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'Ultimate Reference Power Cable',
    cableType: 'power',
    price: usd(9000, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['reference-line', 'magnet-terminations', 'high-current', 'c19-option'],
    provenanceNote:
      'Conductor material, gauge and current rating are inferred from the maker description of the reference series and marked estimated.',
    notes:
      'A heavy mains cord with the reference-series magnet assemblies and a C19 option. The build is elaborate and the cord is very stiff. The engine applies no signal processing to power cords: selecting this changes the chain description and the total system price, and leaves the output bit-identical.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'Helix Interconnect',
    cableType: 'interconnect',
    price: usd(24000, 'APPROXIMATE', 2024, 'Roughly a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'coaxial',
    gauge: 20,
    shielding: 'Braided shield',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['flagship', 'magnet-terminations', 'solid-silver'],
    provenanceNote:
      'Flagship series. Conductor material and gauge are inferred from the maker description and marked estimated; no gauge table or electrical data is published.',
    notes:
      'The flagship, with the largest magnet assemblies in the range and a silver conductor. Silver has about eight percent lower resistivity than copper, which is the entire electrical difference the material makes and amounts to a few milliohms over a metre of thin conductor.',
  }),

  makeCable({
    mfr: 'High Fidelity Cables',
    model: 'Helix Speaker Cable',
    cableType: 'speaker',
    price: usd(32000, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#c2436a',
    tags: ['flagship', 'magnet-terminations', 'solid-silver'],
    provenanceNote:
      'Conductor material and gauge inferred from the maker description of the flagship series and marked estimated; no published electrical figures.',
    notes:
      'The flagship speaker cable: silver conductors at roughly 11 AWG with the largest magnet housings in the range. Silver at that gauge gives about 0.0074 ohm/m of loop resistance against 0.008 for copper. Over 2.5 m that difference is 0.0015 ohm, which against a 4 ohm load is under 0.004 dB.',
  }),

  // =========================================================================
  // Verastarr -- United States, Georgia. The whole range is built from flat
  // foil conductors, which puts every cable here at the low-inductance,
  // high-capacitance end of the geometry trade-off.
  // =========================================================================
  makeCable({
    mfr: 'Verastarr',
    model: 'Copper Foil Speaker Cable',
    cableType: 'speaker',
    price: usd(1200, 'APPROXIMATE', 2024, 'Roughly a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'ribbon',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['entry-line', 'foil-ribbon', 'low-inductance'],
    provenanceNote:
      'Verastarr describes the foil construction and conductor metal but publishes no resistance, inductance or capacitance. Equivalent gauge is inferred from the foil cross-section and marked estimated; all electrical values are derived.',
    notes:
      'Flat copper foil rather than round wire, with the two polarities running wide and close together. That is the lowest-inductance geometry there is -- around 0.05 uH/m against 0.6 for a twisted pair -- and it comes with capacitance an order of magnitude higher, near 620 pF/m. Into a loudspeaker the capacitance is harmless; the low inductance is the point.',
  }),

  makeCable({
    mfr: 'Verastarr',
    model: 'Silver Foil Speaker Cable',
    cableType: 'speaker',
    price: usd(4000, 'APPROXIMATE', 2024, 'Roughly a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'ribbon',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['mid-line', 'foil-ribbon', 'solid-silver'],
    provenanceNote:
      'Foil construction and conductor metal are stated by the maker; equivalent gauge is inferred from the foil cross-section and marked estimated. No published electrical figures.',
    notes:
      'The same ribbon geometry drawn in silver foil. Because geometry sets inductance and capacitance, both are identical to the copper version; the only figure the metal changes is loop resistance, by about eight percent. On a 2.4 m run into 4 ohms that is roughly 0.002 ohm, or under 0.005 dB.',
  }),

  makeCable({
    mfr: 'Verastarr',
    model: 'Grand Illusion Speaker Cable',
    cableType: 'speaker',
    price: usd(8000, 'APPROXIMATE', 2024, 'Roughly a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'ribbon',
    gauge: 11,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['upper-line', 'foil-ribbon', 'solid-silver'],
    provenanceNote:
      'Equivalent gauge inferred from the wider foil section described for this series and marked estimated. No electrical data is published.',
    notes:
      'A wider silver foil than the base series, giving roughly an 11 AWG equivalent section. Widening the foil lowers resistance and, because it also increases the facing area between the two polarities, raises capacitance further. A 2.4 m run of this presents around 1.5 nF to the amplifier, which some heavily fed-back solid-state designs prefer to see through a small series inductor.',
  }),

  makeCable({
    mfr: 'Verastarr',
    model: 'Grand Illusion Signature Speaker Cable',
    cableType: 'speaker',
    price: usd(15000, 'APPROXIMATE', 2024, 'Roughly a 2.4 m (8 ft) terminated pair.'),
    priceLengthM: 2.4,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'ribbon',
    gauge: 10,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['flagship', 'foil-ribbon', 'solid-silver'],
    provenanceNote:
      'Flagship series. Equivalent gauge inferred from the foil section and marked estimated; no published electrical figures.',
    notes:
      'The widest silver foil in the range at roughly a 10 AWG equivalent, giving about 0.0061 ohm/m of loop resistance -- the lowest of any cable in this group. Combined with ribbon inductance near 0.05 uH/m, the series impedance this presents at 20 kHz is dominated by resistance rather than reactance, which is unusual and is a direct consequence of the geometry.',
  }),

  makeCable({
    mfr: 'Verastarr',
    model: 'Copper Foil Interconnect',
    cableType: 'interconnect',
    price: usd(500, 'APPROXIMATE', 2024, 'Roughly a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'ribbon',
    gauge: 22,
    shielding: 'Foil shield with drain',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['entry-line', 'foil-ribbon'],
    provenanceNote:
      'Foil construction is stated by the maker; equivalent gauge and shielding are inferred and marked estimated. No electrical figures are published.',
    notes:
      'Flat foil in an interconnect, which is worth thinking about before buying: ribbon geometry gives around 620 pF/m, so a 1 m run loads the source with roughly 620 pF. Against a 100 ohm solid-state output that corners above 2 MHz and is irrelevant; against a 2.5 kohm tube output it corners near 100 kHz, which is still above the band but is the case to check. The engine computes it either way.',
  }),

  makeCable({
    mfr: 'Verastarr',
    model: 'Grand Illusion Interconnect',
    cableType: 'interconnect',
    price: usd(3500, 'APPROXIMATE', 2024, 'Roughly a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'ribbon',
    gauge: 22,
    shielding: 'Foil shield with drain',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['upper-line', 'foil-ribbon', 'solid-silver'],
    provenanceNote:
      'Conductor metal and foil construction are stated by the maker; equivalent gauge is inferred and marked estimated. No published electrical data.',
    notes:
      'Silver foil in the same ribbon geometry, so the capacitance the source sees is unchanged from the copper version. In a line-level interconnect capacitance is the term with any prospect of mattering, and it is set by the shape of the conductors, not by which metal they are made of.',
  }),

  makeCable({
    mfr: 'Verastarr',
    model: 'Grand Illusion Signature Interconnect',
    cableType: 'interconnect',
    price: usd(6500, 'APPROXIMATE', 2024, 'Roughly a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'ribbon',
    gauge: 21,
    shielding: 'Foil shield with drain',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['flagship', 'foil-ribbon', 'solid-silver'],
    provenanceNote:
      'Flagship interconnect. Equivalent gauge inferred from the wider foil section and marked estimated; nothing electrical is published.',
    notes:
      'The widest silver foil offered in an interconnect. Extra width lowers series resistance, which is already several orders of magnitude below a normal input impedance, and increases capacitance, which is the figure that actually loads the source. That is the trade this geometry always makes.',
  }),

  makeCable({
    mfr: 'Verastarr',
    model: 'Nemesis Power Cord',
    cableType: 'power',
    price: usd(1500, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'ribbon',
    gauge: 11,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['entry-line', 'foil-ribbon', 'high-current'],
    provenanceNote:
      'Foil construction is stated by the maker; equivalent gauge and current rating are inferred from the physical build and marked estimated.',
    notes:
      'A mains cord built from flat copper foil rather than stranded round conductors, at roughly an 11 AWG equivalent for a 15 A circuit. Foil cords are flat and wide, so they route along a wall better than they bend behind a rack. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Verastarr',
    model: 'Grand Illusion Power Cord',
    cableType: 'power',
    price: usd(4500, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'ribbon',
    gauge: 10,
    currentRatingA: 20,
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['upper-line', 'foil-ribbon', 'high-current', 'c19-option'],
    provenanceNote:
      'Equivalent gauge, current rating and connector options are inferred from the physical build and marked estimated; the maker publishes no specification sheet.',
    notes:
      'A wider copper foil cord at roughly a 10 AWG equivalent with a C19 option for 20 A inlets. That is enough conductor for any domestic power amplifier inlet. The engine applies no signal processing to power cords, so this entry contributes construction and price only.',
  }),

  makeCable({
    mfr: 'Verastarr',
    model: 'Grand Illusion Signature Power Cord',
    cableType: 'power',
    price: usd(9000, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'ribbon',
    gauge: 10,
    currentRatingA: 20,
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#b8b8c8',
    tags: ['flagship', 'foil-ribbon', 'solid-silver', 'high-current'],
    provenanceNote:
      'Conductor metal is stated by the maker for this series; equivalent gauge and current rating are inferred and marked estimated.',
    notes:
      'Silver foil at roughly a 10 AWG equivalent, which is where most of the price comes from -- silver at that section is expensive metal. Silver carries current with about eight percent less loss than copper, so the voltage drop under load is marginally lower. The engine applies no signal processing to power cords and does not model mains delivery at all.',
  }),

  // =========================================================================
  // Sablon Audio -- United Kingdom. A small maker whose range is centred on
  // mains cords, with digital and analog cables alongside. Nothing electrical
  // is published, so every entry here is derived and marked estimated.
  // =========================================================================
  makeCable({
    mfr: 'Sablon Audio',
    model: 'Petit Corona Power Cord',
    cableType: 'power',
    price: usd(700, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord; UK maker selling direct, so price varies with exchange rate and connector choice.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 13,
    shielding: 'Shielded with a separate drain',
    termination: ['IEC C13', 'IEC C15', 'Schuko', 'UK 13 A', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#8b5e3c',
    tags: ['entry-line', 'shielded'],
    provenanceNote:
      'Sablon Audio publishes construction descriptions but no gauge table and no electrical figures. Gauge, current rating and geometry are inferred from the physical build and are marked estimated.',
    notes:
      'The entry cord in the range: shielded twisted conductors at roughly 13 AWG with a choice of IEC and wall ends including a UK 13 A fused plug. The engine applies no signal processing to power cords, so this entry records the build and the price and changes nothing in the audio.',
  }),

  makeCable({
    mfr: 'Sablon Audio',
    model: 'Corona Power Cord',
    cableType: 'power',
    price: usd(1100, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Shielded with a separate drain',
    termination: ['IEC C13', 'IEC C15', 'Schuko', 'UK 13 A', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#8b5e3c',
    tags: ['mid-line', 'shielded', 'occ-conductor'],
    provenanceNote:
      'Conductor grade is described by the maker as continuous-cast copper; gauge and current rating are inferred from the physical build and marked estimated.',
    notes:
      'Continuous-cast copper at roughly 12 AWG with the same shielding scheme and connector options as the Petit Corona. Heavier legs mean lower voltage drop under a large current draw. The engine applies no signal processing to power cords.',
  }),

  makeCable({
    mfr: 'Sablon Audio',
    model: 'Panatela Power Cord',
    cableType: 'power',
    price: usd(1600, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Multi-layer shield with a separate drain',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'Schuko', 'UK 13 A', 'NEMA 5-15'],
    confidence: 'ESTIMATED',
    accent: '#8b5e3c',
    tags: ['upper-line', 'shielded', 'occ-conductor', 'c19-option'],
    provenanceNote:
      'Conductor grade is as described by the maker; gauge, current rating and shield construction are inferred from the physical build and marked estimated.',
    notes:
      'The long-running upper cord, with a heavier multi-layer shield and a C19 option. The engine applies no signal processing to power cords. That is not a comment on the workmanship, which is considerable -- it is that a mains cord sits upstream of the rectifier and regulation of whatever it feeds, and there is no mechanism by which it reaches the audio signal.',
  }),

  makeCable({
    mfr: 'Sablon Audio',
    model: 'Panatela Reserva Power Cord',
    cableType: 'power',
    price: usd(2600, 'APPROXIMATE', 2024, 'Roughly a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    shielding: 'Multi-layer shield with a separate drain',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'Schuko', 'UK 13 A', 'NEMA 5-15', 'NEMA 5-20'],
    confidence: 'ESTIMATED',
    accent: '#8b5e3c',
    tags: ['flagship', 'shielded', 'occ-conductor', 'high-current', 'c19-option'],
    provenanceNote:
      'The Reserva designation covers a heavier conductor and revised shielding. Gauge, current rating and shield details are inferred from the physical build and marked estimated; no specification sheet is published.',
    notes:
      'The top mains cord in the range: heavier continuous-cast copper at roughly an 11 AWG equivalent, full multi-layer shielding, and a C19 option for 20 A inlets. Genuinely serious construction to which the engine applies exactly zero digital signal processing.',
  }),

  makeCable({
    mfr: 'Sablon Audio',
    model: 'Panatela Reserva USB',
    cableType: 'usb',
    price: usd(1200, 'APPROXIMATE', 2024, 'Roughly a 1 m assembly.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid, with the bus power conductors run separately from the data pair',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#8b5e3c',
    tags: ['usb-2.0', 'shielded', 'split-power'],
    provenanceNote:
      'Conductor material, gauge and the split power arrangement are inferred from the maker description; no compliance report or electrical sheet is published, so the entry is marked estimated.',
    notes:
      'A 90 ohm USB 2.0 assembly whose distinguishing build feature is that the 5 V bus power conductors are routed in a separate jacket from the data pair rather than bundled with it. The engine applies no signal processing to USB: the link is packetised and error-checked, and an asynchronous DAC clocks the data itself.',
  }),

  makeCable({
    mfr: 'Sablon Audio',
    model: 'Panatela Reserva Ethernet',
    cableType: 'ethernet',
    price: usd(1000, 'APPROXIMATE', 2024, 'Roughly a 1 m patch lead.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Category 7 class shielded twisted pair, RJ45 terminated',
    shielding: 'S/FTP, individually foiled pairs inside an overall braid',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    accent: '#8b5e3c',
    tags: ['shielded', 'stp'],
    provenanceNote:
      'The cable is described as a shielded twisted-pair patch lead assembled and terminated by hand. The exact category certification and conductor gauge are not published, so both are inferred here and the entry is marked estimated.',
    notes:
      'Hand-terminated S/FTP with each pair individually foiled inside an overall braid, on a 100 ohm nominal impedance. The engine applies no signal processing to ethernet: the link is packetised, checksummed and retransmitted on error, and a streamer holds seconds of buffered audio, so there is no analog path between the two ends.',
  }),

  makeCable({
    mfr: 'Sablon Audio',
    model: 'Panatela Reserva Interconnect',
    cableType: 'interconnect',
    price: usd(1800, 'APPROXIMATE', 2024, 'Roughly a 1 m pair; RCA or XLR.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Foil and braid with a separate drain',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#8b5e3c',
    tags: ['upper-line', 'shielded', 'occ-conductor'],
    provenanceNote:
      'Conductor grade is as described by the maker; gauge, geometry and shield construction are inferred from the physical build and marked estimated. No electrical figures are published.',
    notes:
      'A shielded continuous-cast copper twisted pair in single-ended and balanced terminations. A derived 70 pF/m puts a 1 m run at roughly 70 pF of source loading, and the shield-plus-drain arrangement is the part of the build that does real work on a run passing near a mains transformer.',
  }),

  makeCable({
    mfr: 'Sablon Audio',
    model: 'Panatela Reserva Speaker Cable',
    cableType: 'speaker',
    price: usd(3200, 'APPROXIMATE', 2024, 'Roughly a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'braided',
    gauge: 12,
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#8b5e3c',
    tags: ['upper-line', 'braided', 'occ-conductor'],
    provenanceNote:
      'Conductor grade is as described by the maker; gauge and the braid classification are inferred from the physical build and marked estimated. Nothing electrical is published.',
    notes:
      'Continuous-cast copper conductors woven so opposing runs sit adjacent, which is what holds derived inductance near 0.4 uH/m. At 12 AWG a 2.5 m run contributes about 0.026 ohm of loop resistance, and that resistance in series with the driver is the one term in a speaker cable that reliably shows up in a measurement.',
  }),
];
